"use client";

import { useEffect, useRef, useState } from "react";
import { Reveal } from "@/components/ui/Reveal";
import { AnimatedCounter } from "@/components/ui/AnimatedCounter";
import { GOOGLE_REVIEWS_URL, PLACEHOLDER_REVIEWS, REVIEWS } from "@/lib/site-data";

const REVIEW_CARDS = [...REVIEWS, ...PLACEHOLDER_REVIEWS];

// Rotating avatar fills matching the reference design's multicolor accents.
const AVATAR_ACCENTS = [
  "bg-[#6d28d9] text-white",
  "bg-[#92400e] text-white",
  "bg-[#db2777] text-white",
  "bg-[#0f766e] text-white",
];

function getInitials(name: string) {
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? parts[0]?.[1] ?? "")).toUpperCase();
}

function GoogleG({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" className={className}>
      <path
        fill="#FFC107"
        d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.1 8 3l5.7-5.7C34.6 6.5 29.6 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.7-.4-3.5z"
      />
      <path
        fill="#FF3D00"
        d="M6.3 14.7l6.6 4.8C14.6 15.9 18.9 13 24 13c3.1 0 5.8 1.1 8 3l5.7-5.7C34.6 6.5 29.6 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"
      />
      <path
        fill="#4CAF50"
        d="M24 44c5.5 0 10.4-1.9 14.3-5.1l-6.6-5.6C29.6 35 26.9 36 24 36c-5.2 0-9.6-3.3-11.2-8l-6.6 5.1C9.6 39.6 16.3 44 24 44z"
      />
      <path
        fill="#1976D2"
        d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.3 5.8l6.6 5.6C40.5 36.4 44 30.9 44 24c0-1.3-.1-2.7-.4-3.5z"
      />
    </svg>
  );
}

function Star({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className={className}>
      <path d="M10 1.5l2.6 5.6 6.1.6-4.6 4.1 1.4 6-5.5-3.2-5.5 3.2 1.4-6-4.6-4.1 6.1-.6L10 1.5z" />
    </svg>
  );
}

function VerifiedCheck({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 20 20" fill="none" className={className}>
      <circle cx="10" cy="10" r="10" fill="#1a73e8" />
      <path
        d="M6 10.2l2.4 2.4L14 7"
        stroke="white"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ChevronIcon({ dir = 1 }: { dir?: 1 | -1 }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className="h-4 w-4">
      <path
        d={dir === 1 ? "M9 5l7 7-7 7" : "M15 5l-7 7 7 7"}
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

type Review = { name: string; text: string; rating: number };

function ReviewCard({ review, avatarClass }: { review: Review; avatarClass: string }) {
  const textRef = useRef<HTMLParagraphElement>(null);
  const [truncated, setTruncated] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    const el = textRef.current;
    if (!el || expanded) return;
    // Delay layout read to prevent forced reflow thrashing on mount
    const id = requestAnimationFrame(() => {
      setTruncated(el.scrollHeight > el.clientHeight + 1);
    });
    return () => cancelAnimationFrame(id);
  }, [review.text, expanded]);

  return (
    <div
      data-review-card
      className="snap-start shrink-0 w-[85%] sm:w-[calc((100%-20px)/2)] lg:w-[calc((100%-60px)/4)] min-h-[330px] flex flex-col overflow-hidden rounded-2xl bg-white shadow-sm"
    >
      <div
        className="h-1.5 shrink-0"
        style={{
          background: "linear-gradient(to right, #4285f4, #ea4335, #fbbc05, #34a853)",
        }}
      />
      <div className="flex flex-1 flex-col p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <GoogleG className="h-5 w-5" />
            <span className="leading-[1.15]">
              <span className="block text-[10px] text-brand-gray">Posted on</span>
              <span className="block text-sm font-semibold text-[#4285f4]">Google</span>
            </span>
          </div>
          <span className="rounded-full bg-[#eceafb] px-2.5 py-1 font-accent text-[9px] font-bold uppercase text-[#4338ca]">
            Verified Review
          </span>
        </div>

        <div className="mt-3 flex items-center gap-1.5">
          <div className="flex text-[#f5a623]">
            {Array.from({ length: review.rating }).map((_, s) => (
              <Star key={s} className="h-4 w-4" />
            ))}
          </div>
          <VerifiedCheck className="h-3.5 w-3.5" />
        </div>

        <div
          ref={textRef}
          className={`mt-3 text-sm font-semibold text-brand-gray leading-relaxed text-justify [&>p]:mt-2 ${
            expanded ? "" : "line-clamp-4"
          }`}
          dangerouslySetInnerHTML={{ __html: review.text }}
        />
        {(truncated || expanded) && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="mt-1 inline-block self-start text-sm font-semibold text-[#3b3fd6] hover:underline"
          >
            {expanded ? "Read less" : "Read more"}
          </button>
        )}

        <div className="mt-auto flex items-center gap-3 border-t border-brand-gray-light pt-4">
          <span
            className={`flex h-9 w-9 items-center justify-center rounded-full text-xs font-semibold ${avatarClass}`}
          >
            {getInitials(review.name)}
          </span>
          <span className="text-sm font-semibold text-[#2b3990]">{review.name}</span>
        </div>
      </div>
    </div>
  );
}

export function GoogleReviews({
  eyebrow,
  title,
  highlightText,
  ratingValue,
  writeReviewUrl,
  items,
}: {
  eyebrow?: string;
  title?: string;
  highlightText?: string;
  ratingValue?: string;
  writeReviewUrl?: string;
  items?: Array<{ name: string; text: string; rating: number }>;
}) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [canScroll, setCanScroll] = useState(false);

  const finalEyebrow = eyebrow || "Google Reviews";
  const finalTitle = title || "Stories shared by";
  const finalHighlightText = highlightText !== undefined ? highlightText : "our clients";
  const finalRatingValue = ratingValue || "4.8";
  const finalWriteReviewUrl = writeReviewUrl || GOOGLE_REVIEWS_URL;
  const finalItems = items && items.length > 0 ? items : REVIEW_CARDS;

  const parsedRating = parseFloat(finalRatingValue);
  const finalRatingNum = isNaN(parsedRating) ? 4.8 : parsedRating;

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const checkOverflow = () => {
      requestAnimationFrame(() => {
        setCanScroll(el.scrollWidth > el.clientWidth + 1);
      });
    };
    checkOverflow();
    const observer = new ResizeObserver(checkOverflow);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  function scrollByCard(dir: 1 | -1) {
    const el = scrollerRef.current;
    if (!el) return;
    const card = el.querySelector<HTMLElement>("[data-review-card]");
    const amount = card ? card.offsetWidth + 20 : 320;
    el.scrollBy({ left: dir * amount, behavior: "smooth" });
  }

  return (
    <section className="relative overflow-hidden bg-[#0a1120] py-14 sm:py-16">
      <div className="absolute inset-0 bg-gradient-to-br from-[#0a1120] via-[#0d1b30] to-[#0a1120]" />
      <div className="absolute -top-32 -left-20 h-[420px] w-[420px] rounded-full bg-[#2563eb]/15 blur-[120px]" />
      <div className="absolute -bottom-24 -right-10 h-[380px] w-[380px] rounded-full bg-[#06b6d4]/15 blur-[120px]" />
      <div className="absolute inset-0 brand-cell-pattern opacity-[0.06]" />

      <div className="relative w-full px-6 md:px-12 xl:px-[170px]">
        <div className="flex flex-wrap items-start justify-between gap-10 mb-8">
          <Reveal className="max-w-xl">
            <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-black/30 text-white font-medium px-3 py-1.5 text-xs">
              <span className="h-1.5 w-1.5 rounded-full bg-[#22c55e]" />
              {finalEyebrow}
            </span>
            <h2 className="mt-4 text-4xl sm:text-5xl font-semibold text-white">
              {finalTitle}
              {finalHighlightText && (
                <>
                  <br />
                  <span className="text-[#e8394a]">{finalHighlightText}</span>
                </>
              )}
            </h2>
          </Reveal>

          <Reveal delay={0.1} className="flex flex-col items-start sm:items-end gap-4">
            <div className="flex items-center gap-3">
              <div>
                <p className="font-accent text-xs uppercase text-white/60">Google Rating</p>
                <div className="mt-1 flex items-center gap-2">
                  <GoogleG className="h-7 w-7" />
                  <span className="leading-[1.15]">
                    <span className="block text-xs text-white/60">Posted on</span>
                    <span className="block text-lg font-semibold text-[#4285f4]">Google</span>
                  </span>
                </div>
              </div>
              <div className="flex text-[#f5a623]">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} className="h-7 w-7" />
                ))}
              </div>
              <div className="flex items-baseline gap-1">
                <AnimatedCounter
                  value={finalRatingNum}
                  decimals={1}
                  className="text-4xl sm:text-5xl font-heading font-semibold text-white"
                />
                <span className="text-white/50 text-sm">/5</span>
              </div>
            </div>

            <a
              href={finalWriteReviewUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 rounded-full bg-white pl-2 pr-2.5 py-2 shadow-lg transition-transform hover:-translate-y-0.5"
            >
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-gray-light">
                <GoogleG className="h-4 w-4" />
              </span>
              <span className="text-left leading-tight">
                <span className="block font-accent text-[9px] uppercase text-brand-gray/70">
                  Google Review
                </span>
                <span className="block text-xs font-semibold text-[#3b6bf5]">
                  Write a review
                </span>
              </span>
              <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[#3b6bf5] text-white">
                <ChevronIcon />
              </span>
            </a>
          </Reveal>
        </div>

        <div className="relative">
          <div
            ref={scrollerRef}
            className={`no-scrollbar flex gap-5 overflow-x-auto scroll-smooth snap-x snap-mandatory pb-2 ${
              canScroll ? "" : "justify-center"
            }`}
          >
            {finalItems.map((review, i) => (
              <ReviewCard
                key={review.name + i}
                review={review}
                avatarClass={AVATAR_ACCENTS[i % AVATAR_ACCENTS.length]}
              />
            ))}
          </div>

          {canScroll && (
            <>
              <button
                type="button"
                aria-label="Previous reviews"
                onClick={() => scrollByCard(-1)}
                className="hidden sm:flex absolute -left-16 top-1/2 -translate-y-1/2 h-10 w-10 items-center justify-center rounded-full bg-[#12141c] text-white shadow-sm transition-all hover:bg-[#1e2130] hover:shadow-md"
              >
                <ChevronIcon dir={-1} />
              </button>
              <button
                type="button"
                aria-label="Next reviews"
                onClick={() => scrollByCard(1)}
                className="hidden sm:flex absolute -right-16 top-1/2 -translate-y-1/2 h-10 w-10 items-center justify-center rounded-full bg-[#12141c] text-white shadow-sm transition-all hover:bg-[#1e2130] hover:shadow-md"
              >
                <ChevronIcon dir={1} />
              </button>
            </>
          )}
        </div>
      </div>
    </section>
  );
}
